import React, {useEffect, useRef, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Link} from 'react-router-dom';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

const groupMessagesByDate = (messages) => {
    if (!messages || !Array.isArray(messages)) return {};
    return messages.reduce((groups, message) => {
        const date = message.modifiedDate;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
        return groups;
    }, {});
};

const formatDisplayDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

function convertTo12HourFormat(timeString) {
    if (!timeString) return 'No time';
    // Split the input string to get hours, minutes, and seconds
    const [hours, minutes] = timeString.split(':').map(Number);

    // Determine AM or PM suffix
    const suffix = hours >= 12 ? 'PM' : 'AM';

    // Convert hours from 24-hour to 12-hour format
    const hourIn12HrFormat = hours % 12 || 12; // Use 12 for midnight and noon

    // Return formatted time string
    return `${hourIn12HrFormat}:${minutes.toString().padStart(2, '0')} ${suffix}`;
}

// Safe function to get initials
const getInitial = (name) => {
    if (!name || name.trim() === '') return '?';
    return name.trim().charAt(0).toUpperCase();
};

const Conversation = ({
                          smsData,
                          handleSendMessage,
                          getConversationDetails,
                          conversationDetailsLoading,
                          conversationDetails
                      }) => {

    // Add safety check for smsData
    const safeSmsData = Array.isArray(smsData) && smsData.length > 0 ? smsData : [];
    const groupedMessages = groupMessagesByDate(safeSmsData);
    const dates = Object.keys(groupedMessages);

    const [charCount, setCharCount] = useState(0)
    const [message, setMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [maxCount, setMaxCount] = useState(false)

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        getConversationDetails()
        setShow(true);
    }


    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView(); // Removed smooth behavior
    };

    const handleInputChange = (event) => {

        const inputValue = event.target.value;
        if (inputValue.length <= 450) {
            setMessage(inputValue);
            setCharCount(inputValue.length);
            setMaxCount(false)
        } else {
            setMaxCount(true)
        }

    };
    useEffect(() => {
        scrollToBottom();
    }, [smsData]);

    const sendMessage = () => {
        if (message.trim() !== '') {
            //check confirmationrequired is checked or not if checked then add its value to be true
            const confirmationRequired = document.getElementById('confirmationrequired').checked;

            handleSendMessage(message, confirmationRequired);
            setMessage('');
            setCharCount(0);
        }
    };

    const TextWithLinks = ({text, linkColor = 'blue'}) => {
        if (!text) return null;
        // Regex pattern to detect URLs in the text
        const urlPattern = /(https?:\/\/[^\s]+)/g;

        // Split the text based on URLs and create clickable links
        const parts = text.split(urlPattern).map((part, index) => {
            if (urlPattern.test(part)) {
                return (
                    <Link
                        key={index}
                        to="#"
                        style={{color: linkColor, textDecoration: 'underline'}}
                    >
                        {part}
                    </Link>
                );
            } else {
                return part;
            }
        });

        return <>{parts}</>;
    };

    // Check if we have valid data to display
    if (safeSmsData.length === 0) {
        return (
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow">
                            <div className="card-body text-center p-5">
                                <h5>No conversation data available</h5>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="row">
                <div className="col-12">
                    <div className="card shadow">
                        <div className="card-header">
                                <div className='d-flex flex-row align-items-center'>
                                    <div className="symbol symbol-45px symbol-circle me-2" zn_id="155">
                                        <span className="symbol-label bg-light-primary fs-6 fw-bolder" zn_id="157">
                                            {getInitial(safeSmsData[0].customerFirstName)}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="text-gray-800 fw-bold fs-6">
                                            {safeSmsData[0].customerFirstName || 'No first name'} {safeSmsData[0].customerLastName || ''}
                                        </div>
                                        <p className='text-muted'>{safeSmsData[0].customerDob || 'No DOB'}</p>
                                    </div>
                                </div>

                                <div>
                                    <button className="btn btn-sm btn-light-primary mt-4 text-center" onClick={handleShow}>
                                        <i className="bi bi-three-dots"></i>
                                    </button>
                                </div>
                        </div>
                        <div className="card-body bg-light" style={{height: '70vh', overflowY: 'scroll'}}>
                            <div className="d-flex flex-column gap-3 p-3">
                                {dates.map(date => (
                                    <div key={date}>
                                        {/* Date separator */}
                                        <div className='d-flex justify-content-center my-3'>
                                          <span className='badge bg-light text-dark p-2 rounded-pill'>
                                            {formatDisplayDate(date)}
                                          </span>
                                        </div>

                                        {/* Messages */}
                                        {groupedMessages[date].map((message, index) => (
                                            <div
                                                key={index}
                                                className={`d-flex ${message.messageType === 'OUTGOING' ? 'justify-content-end mb-2' : 'justify-content-start mb-2'}`}
                                            >
                                                <OverlayTrigger
                                                    placement={message.messageType === 'OUTGOING' ? 'left' : 'right'}
                                                    overlay={
                                                        <Tooltip id={`tooltip-${message.id || index}`}>
                                                            <div className="text-start small">
                                                                <div>{message.status || 'Unknown'}</div>
                                                                <div>{formatDisplayDate(message.modifiedDate)} {convertTo12HourFormat(message.modifiedTime)}</div>
                                                            </div>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div
                                                        className={`d-flex shadow flex-column p-3 rounded-4 position-relative ${
                                                            message.messageType === 'OUTGOING'
                                                                ? 'bg-primary text-white'
                                                                : 'bg-white border'
                                                        }`}
                                                        style={{maxWidth: '85%'}}
                                                    >
                                                        {/* Message text */}
                                                        <div className="mb-1">
                                                            <TextWithLinks
                                                                text={message.message}
                                                                linkColor={message.messageType === 'OUTGOING' ? 'white' : 'blue'}
                                                            />
                                                        </div>

                                                        {/* Message status and time */}
                                                        <div
                                                            className={`d-flex justify-content-between align-items-end ${
                                                                message.messageType === 'OUTGOING' ? 'text-white-50' : 'text-muted'
                                                            }`}
                                                        >
                                                            <small className="fs-8 fw-bold">
                                                                {convertTo12HourFormat(message.modifiedTime)}
                                                            </small>
                                                            {message.messageType === 'OUTGOING' && (
                                                                <span className="ms-2">
                                                                    {message.status === "DELIVERED" ? (
                                                                        <i className="bi bi-check2-all text-white"></i>
                                                                    ) : (
                                                                        <i className="bi bi-clock-history text-white"></i>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Tail indicator */}
                                                        <div
                                                            className={`position-absolute top-50 ${
                                                                message.messageType === 'OUTGOING'
                                                                    ? 'end-0 translate-x-1'
                                                                    : 'start-0 translate-x-n1'
                                                            }`}
                                                            style={{
                                                                width: 0,
                                                                height: 0,
                                                                borderTop: '8px solid transparent',
                                                                borderBottom: '8px solid transparent',
                                                                borderRight: message.messageType === 'OUTGOING'
                                                                    ? '8px solid var(--bs-primary)'
                                                                    : '8px solid white'
                                                            }}
                                                        />
                                                    </div>
                                                </OverlayTrigger>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                <div ref={messagesEndRef}/>
                            </div>
                        </div>
                    </div>
                    <div className="border-1">
                        <div className="input-group mt-3">
                            {/* Text area */}
                            <textarea className="form-control" rows="2" value={message} onChange={handleInputChange}
                                      placeholder="Type a message" zn_id="1"></textarea>
                            <button className="btn btn-primary" onClick={sendMessage}>Send</button>
                        </div>
                        {/* Message Character count */}
                        {maxCount &&
                            <div className="text-danger ms-3">You have reached the maximum limit of 450 characters.</div>}
                        <div className="text-muted d-flex justify-content-between">
                            <div className='form-check mt-2'>
                                <input type="checkbox" name="confirmationrequired" id="confirmationrequired"
                                       className="form-check-input"/>
                                <label className="form-check-label">Confirmation Required</label>
                            </div>
                            <div className='me-4'>{charCount}/450</div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Details for {safeSmsData[0]?.customer || 'Unknown'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ul className='list-group rounded'>
                        {
                            conversationDetailsLoading ? (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading details...</p>
                                </div>
                            ) : (
                                <>
                                    {conversationDetails && conversationDetails.length > 0 ? (
                                        <div className="row row-cols-1 row-cols-md-2 g-3">
                                            {conversationDetails.map((message, index) => (
                                                <div key={index} className="col">
                                                    <div className="card h-100 border-0 shadow-sm">
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center mb-3">
                                                                <div className="symbol symbol-40px symbol-circle me-3">
                                                                <span className="symbol-label bg-light-primary fs-6 fw-bold">
                                                                    {message.firstName ? message.firstName.charAt(0).toUpperCase() : '?'}
                                                                    {message.lastName ? message.lastName.charAt(0).toUpperCase() : ''}
                                                                </span>
                                                                </div>
                                                                <div>
                                                                    <h6 className="card-title mb-0">
                                                                        {message.firstName || 'Unknown'} {message.lastName || ''}
                                                                    </h6>
                                                                    <small className="text-muted">Patient</small>
                                                                </div>
                                                            </div>
                                                            <hr/>
                                                            <div className="row g-2">
                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center">
                                                                        <i className="bi bi-person-badge text-primary me-2"></i>
                                                                        <div>
                                                                            <small className="text-muted d-block">PMS ID</small>
                                                                            <span className="fw-semibold">{message.customerId || 'N/A'}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-6">
                                                                    <div className="d-flex align-items-center">
                                                                        <i className="bi bi-calendar-event text-primary me-2"></i>
                                                                        <div>
                                                                            <small className="text-muted d-block">Date of Birth</small>
                                                                            <span className="fw-semibold">{message.customerDob || 'N/A'}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="mb-3">
                                                <i className="bi bi-person-x display-4 text-muted"></i>
                                            </div>
                                            <h5 className="text-muted">No details available</h5>
                                            <p className="text-muted">No patient information found for this conversation.</p>
                                        </div>
                                    )}
                                </>
                            )
                        }
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-danger text-white" onClick={handleClose}>
                        <i className="bi bi-x-lg"></i> Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Conversation;